import React, { useState, useMemo, useRef } from 'react';
import { formatDateShort, toLocalISO } from '../../utils/dateFormat';
import { BTN_XS } from '../../constants/buttons';
import HireCard from './HireCard';
import { openHireClass, openHireProps, useScrollOpenHireIntoView } from './hireHighlight';

/**
 * CalendarView - Month grid calendar showing hire events.
 * Green dots for hire starts, red dots for hire ends.
 * Red highlight for overdue items, amber highlight for items ending within 3 days.
 * Click a day to see a popup listing all hires active/starting/ending on that day.
 */

// `openItemId` (optional, 2026-09-28): the hire the Table has open. The
// calendar opens on a month that shows it (today's month when the hire
// overlaps it, else the hire's first month), marks its chips with
// `hireHighlight.js`'s ring and scrolls the first into view. Absent → unchanged.
function CalendarView({ events, onViewOrder, canManage, onExtend, onOffHire, onReHire, onOpenHire, openItemId = null }) {
  const gridRef = useRef(null);
  const [currentDate, setCurrentDate] = useState(() => openHireMonth(events, openItemId) || new Date());
  useScrollOpenHireIntoView(openItemId, gridRef);
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Build lookup maps: which events start, end, or are active on each day
  const { dayMap, startMap, endMap } = useMemo(() => {
    const dayMap = {};   // dateStr -> [events active on that day]
    const startMap = {}; // dateStr -> [events starting on that day]
    const endMap = {};   // dateStr -> [events ending on that day]

    if (!events || events.length === 0) return { dayMap, startMap, endMap };

    // Only process events that overlap with current month view
    const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    for (const ev of events) {
      if (!ev.start || !ev.end) continue;
      // Skip events that don't overlap with this month at all
      if (ev.end < monthStart || ev.start > monthEnd) continue;

      // Mark start day
      if (ev.start >= monthStart && ev.start <= monthEnd) {
        if (!startMap[ev.start]) startMap[ev.start] = [];
        startMap[ev.start].push(ev);
      }

      // Mark end day
      if (ev.end >= monthStart && ev.end <= monthEnd) {
        if (!endMap[ev.end]) endMap[ev.end] = [];
        endMap[ev.end].push(ev);
      }

      // Mark all active days within this month
      const activeStart = ev.start > monthStart ? ev.start : monthStart;
      const activeEnd = ev.end < monthEnd ? ev.end : monthEnd;

      let cursor = new Date(activeStart + 'T00:00:00');
      const endD = new Date(activeEnd + 'T00:00:00');
      while (cursor <= endD) {
        const ds = toLocalISO(cursor);
        if (!dayMap[ds]) dayMap[ds] = [];
        dayMap[ds].push(ev);
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    return { dayMap, startMap, endMap };
  }, [events, year, month]);

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const startDow = (firstOfMonth.getDay() + 6) % 7; // Monday = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    // Empty cells for days before the 1st
    for (let i = 0; i < startDow; i++) {
      days.push({ day: null, dateStr: null });
    }

    const todayStr = toLocalISO(new Date());

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const activeEvents = dayMap[dateStr] || [];
      const starts = startMap[dateStr] || [];
      const ends = endMap[dateStr] || [];

      // Check for overdue items active on this day
      const hasOverdue = activeEvents.some(e => e.status === 'overdue');

      // Check for items ending within 3 days from today
      const threeDaysFromNow = new Date();
      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
      const threeDaysStr = toLocalISO(threeDaysFromNow);
      const hasEndingSoon = activeEvents.some(e =>
        e.status === 'on_hire' && e.end >= todayStr && e.end <= threeDaysStr
      );

      days.push({
        day: d,
        dateStr,
        isToday: dateStr === todayStr,
        activeEvents,
        starts,
        ends,
        hasOverdue,
        hasEndingSoon,
      });
    }

    return days;
  }, [year, month, dayMap, startMap, endMap]);

  // Navigate months
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDay(null);
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDay(null);
  };
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  const monthName = currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  // Selected day data
  const selectedDayData = selectedDay
    ? calendarDays.find(d => d.dateStr === selectedDay)
    : null;

  return (
    <div data-testid="calendar-view">
      {/* Calendar header with navigation */}
      <div className="flex items-center justify-between mb-4 bg-white rounded-lg shadow px-4 py-3" data-section-title>
        <button
          onClick={prevMonth}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          data-testid="calendar-prev-month"
          aria-label="Previous month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200" data-testid="calendar-month-label">
            {monthName}
          </h3>
          <button
            onClick={goToToday}
            className={BTN_XS.primary}
            data-testid="calendar-today-btn"
          >
            Today
          </button>
        </div>

        <button
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          data-testid="calendar-next-month"
          aria-label="Next month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-3 px-1 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"></span> Hire starts
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block"></span> Hire ends
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block"></span> Overdue
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-amber-100 border border-amber-300 inline-block"></span> Ending soon
        </span>
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Day of week headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="text-center text-base font-semibold text-gray-500 dark:text-gray-400 py-4">
              {day}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7" ref={gridRef}>
          {calendarDays.map((cell, idx) => {
            if (!cell.day) {
              return <div key={`empty-${idx}`} className="min-h-[110px] md:min-h-[140px] bg-gray-50 border-b border-r border-gray-100" />;
            }

            // Determine background highlight
            let bgClass = 'bg-white';
            if (cell.hasOverdue) bgClass = 'bg-red-50';
            else if (cell.hasEndingSoon) bgClass = 'bg-amber-50';

            const isSelected = selectedDay === cell.dateStr;
            const borderClass = isSelected
              ? 'ring-2 ring-blue-500 ring-inset'
              : '';

            return (
              <div
                key={cell.dateStr}
                className={`min-h-[110px] md:min-h-[140px] ${bgClass} border-b border-r border-gray-100 p-1 cursor-pointer hover:bg-opacity-80 transition-colors ${borderClass}`}
                onClick={() => setSelectedDay(isSelected ? null : cell.dateStr)}
                data-testid={`calendar-day-${cell.dateStr}`}
              >
                {/* Day number */}
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-lg font-medium ${
                    cell.isToday
                      ? 'bg-blue-600 text-white w-9 h-9 rounded-full flex items-center justify-center'
                      : 'text-gray-700 dark:text-gray-300 pl-1'
                  }`}>
                    {cell.day}
                  </span>
                  {/* Active count badge */}
                  {cell.activeEvents.length > 0 && (
                    <span className="text-[10px] font-medium text-gray-400 pr-1">
                      {cell.activeEvents.length}
                    </span>
                  )}
                </div>

                {/* Dot indicators */}
                <div className="flex flex-wrap gap-1 px-1">
                  {cell.starts.length > 0 && (
                    <span
                      className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"
                      title={`${cell.starts.length} hire(s) starting`}
                      data-testid={`green-dot-${cell.dateStr}`}
                    ></span>
                  )}
                  {cell.ends.length > 0 && (
                    <span
                      className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"
                      title={`${cell.ends.length} hire(s) ending`}
                      data-testid={`red-dot-${cell.dateStr}`}
                    ></span>
                  )}
                </div>

                {/* Mini event list on desktop */}
                <div className="hidden md:block mt-1.5 space-y-1 overflow-hidden">
                  {cell.activeEvents.slice(0, 3).map(ev => (
                    <div
                      key={ev.id}
                      className={`text-xs px-1.5 py-1 rounded truncate bg-slate-700/30 ${
                        ev.status === 'overdue'
                          ? 'text-red-700 dark:text-red-400'
                          : ev.status === 'on_hire'
                          ? 'text-blue-700 dark:text-blue-400'
                          : ev.status === 'pending'
                          ? 'text-amber-700 dark:text-amber-400'
                          : 'text-gray-600 dark:text-gray-400'
                      } ${openHireClass(ev.item_id, openItemId)}`}
                      title={ev.title}
                      data-testid={`calendar-chip-${cell.dateStr}-${ev.item_id}`}
                      {...openHireProps(ev.item_id, openItemId)}
                    >
                      {ev.title}
                    </div>
                  ))}
                  {cell.activeEvents.length > 3 && (
                    <div className="text-xs text-gray-400 pl-1">
                      +{cell.activeEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day popup - shows when a day is clicked */}
      {selectedDayData && (
        <DayPopup
          dateStr={selectedDayData.dateStr}
          starts={selectedDayData.starts}
          ends={selectedDayData.ends}
          activeEvents={selectedDayData.activeEvents}
          onClose={() => setSelectedDay(null)}
          onViewOrder={onViewOrder}
          onOpenHire={onOpenHire}
          canManage={canManage}
          onExtend={onExtend}
          onOffHire={onOffHire}
          onReHire={onReHire}
          openItemId={openItemId}
        />
      )}
    </div>
  );
}

/**
 * The month to open on for the open hire: `null` (→ today) when there is no
 * open hire, it is not among `events`, or it overlaps today's month already;
 * otherwise the 1st of the hire's START month.
 */
function openHireMonth(events, openItemId) {
  if (openItemId == null || !Array.isArray(events)) return null;
  const ev = events.find((e) => e.item_id === openItemId && e.start && e.end);
  if (!ev) return null;
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (!(ev.end < `${ym}-01` || ev.start > `${ym}-${String(lastDay).padStart(2, '0')}`)) return null;
  const [y, m] = ev.start.split('-').map(Number);
  return Number.isFinite(y) && Number.isFinite(m) ? new Date(y, m - 1, 1) : null;
}

/**
 * Popup showing all hires for a selected day.
 */
function DayPopup({ dateStr, starts, ends, activeEvents, onClose, onViewOrder, onOpenHire, canManage, onExtend, onOffHire, onReHire, openItemId = null }) {
  const formattedDate = formatDateShort(dateStr);

  // Categorize events for this day
  const startIds = new Set(starts.map(e => e.id));
  const endIds = new Set(ends.map(e => e.id));

  // Active but not starting or ending on this day
  const ongoing = activeEvents.filter(e => !startIds.has(e.id) && !endIds.has(e.id));

  return (
    <div
      className="mt-4 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      data-testid="calendar-day-popup"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-lg" data-section-title>
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200" data-testid="popup-date">
          {formattedDate}
        </h4>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          data-testid="popup-close"
        >
          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-4 max-h-[400px] overflow-y-auto">
        {activeEvents.length === 0 && starts.length === 0 && ends.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No hire events on this day.</p>
        ) : (
          <div className="space-y-4">
            {/* Starting */}
            {starts.length > 0 && (
              <div data-testid="popup-section-starting">
                <h5 className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Starting ({starts.length})
                </h5>
                {starts.map(ev => (
                  <HireCard key={`start-${ev.item_id}`} item={ev} canManage={canManage} onExtend={onExtend} onOffHire={onOffHire} onReHire={onReHire} onViewOrder={onViewOrder} onOpenHire={onOpenHire} openItemId={openItemId} />
                ))}
              </div>
            )}

            {/* Ending */}
            {ends.length > 0 && (
              <div data-testid="popup-section-ending">
                <h5 className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Ending ({ends.length})
                </h5>
                {ends.map(ev => (
                  <HireCard key={`end-${ev.item_id}`} item={ev} canManage={canManage} onExtend={onExtend} onOffHire={onOffHire} onReHire={onReHire} onViewOrder={onViewOrder} onOpenHire={onOpenHire} openItemId={openItemId} />
                ))}
              </div>
            )}

            {/* Ongoing / Active */}
            {ongoing.length > 0 && (
              <div data-testid="popup-section-active">
                <h5 className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  Active ({ongoing.length})
                </h5>
                {ongoing.map(ev => (
                  <HireCard key={`active-${ev.item_id}`} item={ev} canManage={canManage} onExtend={onExtend} onOffHire={onOffHire} onReHire={onReHire} onViewOrder={onViewOrder} onOpenHire={onOpenHire} openItemId={openItemId} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CalendarView;
